import { API_BASE_URL } from './apiUrl';

export const MOCKUP_CUSTOMIZATION_DRAFT_KEY = 'mockupCustomizationDraft';
export const SELECTED_TEMPLATE_DETAILS_KEY = 'selectedTemplateDetails';

export interface SelectedTemplateDetails {
  id: string;
  category: string;
  name: string;
  description: string;
  thumbnailUrl?: string | null;
  isPro?: boolean;
}

export interface MockupCustomizationDraft {
  template: string;
  primaryImageId: string;
  secondaryImageId: string | null;
  values: {
    backgroundColor: string;
    useBackgroundColor: boolean;
    backgroundDescription: string;
    productColor: string;
    useProductColor: boolean;
    showShadow: boolean;
    showReflection: boolean;
    glossyFinish: boolean;
    embossEffect: boolean;
    gradientOverlay: boolean;
    productTitle: string;
    productSubtitle: string;
    customPrompt: string;
    scale: number;
    aspectRatio: string;
  };
  updatedAt: string;
}

export const readSelectedTemplateDetails = (): SelectedTemplateDetails | null => {
  const rawValue = sessionStorage.getItem(SELECTED_TEMPLATE_DETAILS_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as SelectedTemplateDetails;
  } catch (error) {
    console.error('Failed to parse selected template details:', error);
    sessionStorage.removeItem(SELECTED_TEMPLATE_DETAILS_KEY);
    return null;
  }
};

export const writeSelectedTemplateDetails = (details: SelectedTemplateDetails) => {
  sessionStorage.setItem(SELECTED_TEMPLATE_DETAILS_KEY, JSON.stringify(details));
};

export const resolveTemplateThumbnailUrl = (thumbnailUrl?: string | null) => {
  if (!thumbnailUrl) {
    return '';
  }

  return thumbnailUrl.startsWith('http://') || thumbnailUrl.startsWith('https://')
    ? thumbnailUrl
    : `${API_BASE_URL}${thumbnailUrl}`;
};

export const formatTemplateName = (template?: string | null) => {
  if (!template) {
    return 'No template selected';
  }

  return template
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const readCustomizationDraft = (): MockupCustomizationDraft | null => {
  const rawDraft = sessionStorage.getItem(MOCKUP_CUSTOMIZATION_DRAFT_KEY);
  if (!rawDraft) {
    return null;
  }

  try {
    return JSON.parse(rawDraft) as MockupCustomizationDraft;
  } catch (error) {
    console.error('Failed to parse customization draft:', error);
    sessionStorage.removeItem(MOCKUP_CUSTOMIZATION_DRAFT_KEY);
    return null;
  }
};

export const writeCustomizationDraft = (draft: MockupCustomizationDraft) => {
  sessionStorage.setItem(MOCKUP_CUSTOMIZATION_DRAFT_KEY, JSON.stringify(draft));
};

export const clearCustomizationDraft = () => {
  sessionStorage.removeItem(MOCKUP_CUSTOMIZATION_DRAFT_KEY);
};

export const getPrimarySourceLabel = () => {
  if (sessionStorage.getItem('useBrandLogo') === 'true') {
    return 'Brand logo';
  }

  if (sessionStorage.getItem('isAssetLibrary') === 'true') {
    return 'Asset library';
  }

  if (sessionStorage.getItem('primaryImageId')) {
    return 'Uploaded asset';
  }

  return 'Missing';
};

export const formatSavedTimestamp = (value?: string | null) => {
  if (!value) {
    return 'Not saved yet';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Not saved yet';
  }

  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};
